/** Agreement statuses that can be revised before the customer signs. */
const EDITABLE_AGREEMENT_STATUSES = ['draft', 'awaiting_accept'];

const isEditableAgreementStatus = (status) =>
  EDITABLE_AGREEMENT_STATUSES.includes(String(status || '').toLowerCase());

/**
 * Map a contract row from GET /my/agreements into NewAgreement form state.
 * @param {object} contract
 */
const contractToFormState = (contract) => {
  if (!contract) return null;

  const rows = contract.lineItems || [];
  const planLine = rows.find((row) => row.catalogPlanId);
  const lineItems = rows
    .filter((row) => row.kind === 'one_off')
    .map((row) => ({
      kind: 'one_off',
      unitAmount: row.unitAmount != null && row.unitAmount !== '' ? row.unitAmount : '',
      quantity: row.quantity != null ? row.quantity : 1,
      description: row.description || '',
    }));

  const term = contract.term || {};
  const indexationPolicy = contract.indexationPolicy || { kind: 'none' };

  return {
    type: contract.type || 'Direct',
    customerOrganisationName: contract.customerOrganisationName || '',
    customerFirstName: contract.customerFirstName || '',
    customerLastName: contract.customerLastName || '',
    customerEmail: contract.customerEmail || '',
    customerRegisteredCountry: contract.customerRegisteredCountry || '',
    customerBillingCountry: contract.customerBillingCountry || '',
    billingCurrency: contract.billingCurrency || '£',
    whiteLabelId: contract.whiteLabelId != null ? String(contract.whiteLabelId) : '0',
    consultantId: contract.consultantId != null ? String(contract.consultantId) : 'NONE',
    payMode: contract.payMode || 'invoice',
    catalogPlanId: contract.catalogPlanId || planLine?.catalogPlanId || 'measure',
    fteBand: contract.fteBand || planLine?.fteBand || '1-10',
    addOnIds:
      contract.catalogAddOnIds || rows.filter((row) => row.catalogAddOnId).map((row) => row.catalogAddOnId),
    priceVariations: Object.fromEntries(
      rows
        .filter((row) => row.priceVaried && (row.catalogPlanId || row.catalogAddOnId))
        .map((row) => [
          row.catalogPlanId ? 'plan' : row.catalogAddOnId,
          { agreedUnitAmount: String(row.unitAmount), duration: row.priceVariationDuration || 'term' },
        ])
    ),
    priceVariationReason: contract.priceVariation?.reason || '',
    partnerTerms: {
      licenceFee: contract.partnerTerms?.licenceFee ?? '',
      licenceFeeFrequency: contract.partnerTerms?.licenceFeeFrequency || 'annual',
      discountPercent: contract.partnerTerms?.discountPercent ?? '',
      minimumCommitment: contract.partnerTerms?.minimumCommitment ?? '',
      termMonths: contract.partnerTerms?.termMonths || 12,
      startDate: contract.partnerTerms?.requestedStartDate || contract.partnerTerms?.startDate || '',
    },
    ambassadorTerms: {
      revenueSharePeriods: contract.ambassadorTerms?.revenueSharePeriods || [],
      minimumPortfolio: contract.ambassadorTerms?.minimumPortfolio ?? '',
      payoutCurrency: contract.ambassadorTerms?.payoutCurrency || 'GBP',
    },
    lineItems,
    term: {
      type: term.type || 'committed',
      lengthMonths: term.lengthMonths != null ? term.lengthMonths : 36,
      startDate: term.startDate || '',
      noticePeriodDays: term.noticePeriodDays != null ? term.noticePeriodDays : 60,
    },
    indexationPolicy: {
      kind: indexationPolicy.kind || 'none',
      floorPercent: indexationPolicy.floorPercent != null ? indexationPolicy.floorPercent : 5,
      cpiSource: indexationPolicy.cpiSource || 'UK_CPIH',
      noticeDays: indexationPolicy.noticeDays != null ? indexationPolicy.noticeDays : 30,
    },
    linkedExistingOrganisation: !!contract.linkedExistingOrganisation,
    linkedOrganisationId: contract.organisationId || contract.organisation || '',
    replaceExistingSubscription: !!contract.replaceExistingSubscription,
    endClientDefaultPlanType:
      contract.endClientDefaultPlanType === 'client_subscription' ? 'client_subscription' : 'per_report',
    perReportFeeVal:
      contract.perReportFeeVal != null && contract.perReportFeeVal !== ''
        ? String(contract.perReportFeeVal)
        : '',
  };
};

export { EDITABLE_AGREEMENT_STATUSES, isEditableAgreementStatus, contractToFormState };
